// import components
import DrawerFooter from '../Drawer/DrawerFooter';
import { DrawerContext } from '../Drawer/DrawerContext';
import { IconButton, Typography } from '@mui/material';

// import context
import { MultiStepFormContext, useMultiStepFormContext } from './context';

// import enums
import { Status } from './MultiStepForm.enums';

// import external libraries
import { path } from 'ramda';
import { FormProvider } from 'react-hook-form';
import React, { useContext, useEffect, useMemo, useState } from 'react';

// import styles
import {
  StyledFormSectionMainBox,
  StyledMultiStepForm,
  StyledSection,
  StyledShowPreviewBox,
  StyledShowPreviewLeftBox
} from './MultiStepForm.styles';

// import types
import {
  MultiStepFormProps,
  MultiStepFormSectionProps
} from './MultiStepForm.types';
import { Icon } from '@sdb/planning-ui';

const PREFIX = 'MultiStepForm';

const classes = {
  fixedHeight: `${PREFIX}-fixedHeight`,
  autoHeight: `${PREFIX}-autoHeight`
};

function MultiStepFormSectionPreview({
  children,
  preview,
  isPreview,
  isEditable
}: {
  children: React.ReactNode;
  preview: React.ReactNode;
  isPreview: boolean;
  isEditable: boolean;
}) {
  const {
    formState: { submitCount }
  } = useMultiStepFormContext();

  const [editing, setEditing] = useState<boolean>(isPreview);

  useEffect(() => {
    setEditing(false);
  }, [submitCount]);

  function onEditClick() {
    setEditing(!editing);
  }

  return (
    <>
      {!editing ? (
        <StyledFormSectionMainBox>
          {preview}
          {isEditable && (
            <IconButton onClick={onEditClick} size="large">
              <Icon type="pen" />
            </IconButton>
          )}
        </StyledFormSectionMainBox>
      ) : (
        children
      )}
    </>
  );
}

function MultiStepFormSection({
  children,
  title,
  validateFields = [],
  preview,
  isPreview = false,
  isEditable = true,
  isImportant = false,
  fullWidth = false,
  ...rest
}: MultiStepFormSectionProps) {
  const {
    previewMode,
    formState: { errors },
    getFieldState,
    watch,
    getValues
  } = useMultiStepFormContext();

  const [status, setStatus] = useState<Status>(Status.Pending);

  useEffect(() => {
    if (
      validateFields.every((field) => {
        const fieldState = getFieldState(field);
        const value = getValues(field);

        const BoolValue = Array.isArray(value)
          ? value.length > 0
          : Boolean(value);

        // Check if user has entered a value and if the value is valid
        return BoolValue && !fieldState.invalid;
      })
    ) {
      setStatus(Status.Success);
      return;
    }

    if (validateFields.some((field) => path(field?.split('.'), errors))) {
      setStatus(Status.Error);
      return;
    }

    setStatus(Status.Pending);
  }, [watch()]);

  const showPreview = isPreview || previewMode;

  const selectedIcon = useMemo(
    () =>
      status === 'primary'
        ? 'circle-check' // should be a solid circle check icon
        : status === 'disabled'
        ? 'circle-check'
        : status === 'error'
        ? 'circle-xmark'
        : 'circle-check',
    [status]
  );

  return (
    <StyledSection {...rest}>
      <StyledShowPreviewLeftBox fullWidth={fullWidth}>
        <Icon
          type={isImportant ? 'circle-exclamation' : selectedIcon}
          color={isImportant ? Status.Warning : status}
        />
        <Typography variant="h6">{title}</Typography>
      </StyledShowPreviewLeftBox>
      <StyledShowPreviewBox>
        {!showPreview && isEditable ? (
          children
        ) : (
          <MultiStepFormSectionPreview
            preview={preview}
            isPreview={isPreview}
            isEditable={isEditable}
          >
            {children}
          </MultiStepFormSectionPreview>
        )}
      </StyledShowPreviewBox>
    </StyledSection>
  );
}

function MultiStepForm({
  children,
  methods,
  onSubmit,
  fixedHeight = true,
  previewMode = false,
  fullHeight = true,
  style
}: MultiStepFormProps) {
  const { onInnerFormChange } = useContext(DrawerContext);

  useEffect(() => {
    onInnerFormChange?.(methods.formState);
  }, [methods.formState.isDirty]);

  return (
    <MultiStepFormContext.Provider value={{ previewMode }}>
      <FormProvider {...methods}>
        <StyledMultiStepForm
          className={fixedHeight ? classes.fixedHeight : classes.autoHeight}
          onSubmit={methods.handleSubmit(onSubmit)}
          noValidate
          fullHeight={fullHeight}
          {...(style ? { style } : {})}
        >
          {children}
        </StyledMultiStepForm>
      </FormProvider>
    </MultiStepFormContext.Provider>
  );
}

MultiStepForm.Section = MultiStepFormSection;
MultiStepForm.SectionPreview = MultiStepFormSectionPreview;
MultiStepForm.Actions = DrawerFooter;

export default MultiStepForm;
